import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  MessageCircle, X, Send, ChevronLeft, Plus, Search, Loader2, AlertTriangle, Mail, MailOpen,
  EyeOff, ShieldAlert,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { canMessage, filterReceivers, broadcastScopes, canBroadcast } from '../lib/permissions';
import {
  listMessages, readMessage, sendMessage, sendMessageToWorkplace, sendMessageToCompany,
  sendAnonymousMessage, listAnonymousMessages,
  searchWorkers, searchCompanies, getWorkplaces,
} from '../services/api';
import { readSent, appendSentMessage, subscribeToSent } from '../lib/sentMessagesCache';
import { Building2, MapPin, Users as UsersIcon, User as UserIcon, Globe } from 'lucide-react';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// Trabajadores y supervisores (type 0 y 3) pueden enviar denuncias anónimas.
const canSendAnonymous = (user) => user && (user.type === 0 || user.type === 3);
// Admin empresa y admin sede (type 1 y 2) reciben denuncias anónimas.
const canReceiveAnonymous = (user) => user && (user.type === 1 || user.type === 2);

// Destinatarios válidos para denuncia anónima: admin empresa o admin sede.
// Sigue las mismas reglas de scope que canMessage (misma empresa; admin sede sólo
// si está en la sede del remitente o si es admin empresa).
const filterAnonymousReceivers = (sender, candidates) => {
  if (!sender || !Array.isArray(candidates)) return [];
  return candidates.filter((c) => {
    if (c.type !== 1 && c.type !== 2) return false;
    return canMessage(sender, c);
  });
};

const POLL_MS = 30000;

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const today = now.toDateString() === d.toDateString();
  if (today) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const sameYear = now.getFullYear() === d.getFullYear();
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// ─── Vista: Bandeja de denuncias anónimas (solo admins) ───────────────────
// No se agrupa por remitente porque es anónimo: lista plana con la denuncia
// completa expandida (no hay conversación posible, no se puede responder).
const AnonymousInbox = ({ anonMessages, onMarkRead, onBackToInbox }) => {
  // Marcamos como leídas al abrir la vista — al igual que en una conversación
  // normal. Sin volver atrás y entrar, no se considera "leída".
  useEffect(() => {
    const toRead = anonMessages.filter((m) => !m.read);
    if (toRead.length === 0) return;
    Promise.all(toRead.map((m) => readMessage(m.id).catch(() => {})))
      .then(() => onMarkRead && onMarkRead());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sorted = useMemo(
    () => [...anonMessages].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
    [anonMessages]
  );

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <button onClick={onBackToInbox} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Confidencial</div>
          <div className="text-sm font-display font-bold text-ink truncate flex items-center gap-1.5">
            <ShieldAlert size={14} className="text-warning" />
            Denuncias anónimas
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {sorted.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-muted">
            <ShieldAlert size={28} className="mx-auto text-ink-muted/50 mb-2" />
            No hay denuncias anónimas recibidas.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {sorted.map((m) => (
              <li key={m.id} className={`px-4 py-3 ${!m.read ? 'bg-warning/5' : 'bg-white'}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-warning/15 text-warning flex items-center justify-center shrink-0">
                    <EyeOff size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <div className="text-sm font-bold text-ink">Remitente anónimo</div>
                      <div className="text-[11px] text-ink-muted ml-auto tabular-nums shrink-0">
                        {fmtDateTime(m.timestamp)}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-ink whitespace-pre-wrap break-words">
                      {m.content}
                    </div>
                    {!m.read && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-warning">
                        <span className="w-1.5 h-1.5 rounded-full bg-warning" /> Nueva
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="border-t border-slate-200 px-3 py-3 text-center text-[11px] text-ink-muted bg-slate-50">
        No es posible responder a una denuncia anónima.
      </div>
    </>
  );
};

// ─── Vista: Composer denuncia anónima (solo workers/supervisores) ─────────
const AnonymousComposer = ({ candidates, currentUser, onBack, onSend }) => {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [candidates, query]);

  const handleSend = async () => {
    if (!picked || !text.trim()) return;
    setSending(true);
    setError(null);
    try {
      await onSend(picked.id, text.trim());
      toast.success(FEEDBACK.anonymousSent);
      setText('');
      setPicked(null);
      onBack();
    } catch (err) {
      setError(err.message || 'No se pudo enviar la denuncia.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Confidencial</div>
          <div className="text-sm font-display font-bold text-ink truncate flex items-center gap-1.5">
            <ShieldAlert size={14} className="text-warning" />
            Denuncia anónima
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-slate-200 bg-warning/5 text-[11px] text-ink leading-relaxed">
        <div className="flex items-start gap-2">
          <EyeOff size={14} className="text-warning shrink-0 mt-0.5" />
          <div>
            Tu identidad NO se enviará al destinatario. La denuncia llegará marcada
            como anónima a un admin de empresa o admin de sede de tu organización.
          </div>
        </div>
      </div>

      {!picked ? (
        <>
          <div className="px-3 py-2 border-b border-slate-200">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar admin destinatario"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-muted">
                {candidates.length === 0
                  ? 'No hay admins disponibles en tu empresa.'
                  : 'Ningún admin coincide con la búsqueda.'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setPicked(c)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-warning/15 text-warning flex items-center justify-center text-xs font-bold">
                        {c.type === 1 ? <Building2 size={14} /> : <MapPin size={14} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink truncate">{c.name}</div>
                        <div className="text-[11px] text-ink-muted truncate">
                          {c.type === 1 ? 'Admin empresa' : 'Admin sede'} · {c.email}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-warning/15 text-warning flex items-center justify-center text-xs font-bold">
              {picked.type === 1 ? <Building2 size={14} /> : <MapPin size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate">{picked.name}</div>
              <div className="text-[11px] text-ink-muted truncate">
                {picked.type === 1 ? 'Admin empresa' : 'Admin sede'}
              </div>
            </div>
            <button onClick={() => setPicked(null)} className="text-[11px] text-ink-muted hover:text-ink underline">
              Cambiar
            </button>
          </div>
          <div className="flex-1 px-3 py-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Describe los hechos con el mayor detalle posible…"
              rows={8}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-warning text-sm resize-none"
              autoFocus
            />
            {error && <div className="text-[11px] text-danger mt-1.5">{error}</div>}
          </div>
          <div className="border-t border-slate-200 px-3 py-2 flex justify-end gap-2">
            <button
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-warning text-white hover:bg-warning/90 disabled:opacity-50"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
              Enviar anónimamente
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Vista: Buzón ──────────────────────────────────────────────────────────
const Inbox = ({ messages, workersById, onOpenConversation, onNew, onNewAnonymous, onOpenAnonymousInbox, anonUnreadCount, currentUserId, showAnonymousSendButton, showAnonymousInboxButton }) => {
  // Agrupamos por "el otro" (en buzón siempre el sender), y mostramos último mensaje + nº no leídos.
  const conversations = useMemo(() => {
    const byOther = new Map();
    for (const m of messages) {
      const otherId = Number(m.senderId) === Number(currentUserId) ? Number(m.receiverId) : Number(m.senderId);
      if (!byOther.has(otherId)) byOther.set(otherId, { otherId, last: m, unread: 0 });
      const entry = byOther.get(otherId);
      if (new Date(m.timestamp) > new Date(entry.last.timestamp)) entry.last = m;
      if (!m.read && Number(m.receiverId) === Number(currentUserId)) entry.unread += 1;
    }
    return Array.from(byOther.values()).sort((a, b) => new Date(b.last.timestamp) - new Date(a.last.timestamp));
  }, [messages, currentUserId]);

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 gap-2">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Mensajes</div>
          <div className="text-sm font-display font-bold text-ink">Buzón</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {showAnonymousInboxButton && (
            <button
              onClick={onOpenAnonymousInbox}
              title="Denuncias anónimas recibidas"
              className="relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning/15 text-warning hover:bg-warning/25"
            >
              <ShieldAlert size={12} /> Denuncias
              {anonUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-warning text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                  {anonUnreadCount > 99 ? '99+' : anonUnreadCount}
                </span>
              )}
            </button>
          )}
          {showAnonymousSendButton && (
            <button
              onClick={onNewAnonymous}
              title="Enviar denuncia anónima"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning/15 text-warning hover:bg-warning/25"
            >
              <ShieldAlert size={12} /> Denuncia
            </button>
          )}
          <button onClick={onNew} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark">
            <Plus size={12} /> Nuevo
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-muted">
            <Mail size={28} className="mx-auto text-ink-muted/50 mb-2" />
            No tienes mensajes todavía.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {conversations.map((c) => {
              const w = workersById.get(c.otherId);
              const isFromMe = Number(c.last.senderId) === Number(currentUserId);
              return (
                <li key={c.otherId}>
                  <button
                    onClick={() => onOpenConversation(c.otherId)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3"
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      c.unread > 0 ? 'bg-primary text-white' : 'bg-primary-soft text-primary-darker'
                    }`}>
                      {(w?.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <div className={`text-sm truncate ${c.unread > 0 ? 'font-bold text-ink' : 'font-semibold text-ink'}`}>
                          {w?.name || `Usuario #${c.otherId}`}
                        </div>
                        <div className="text-[11px] text-ink-muted shrink-0 ml-auto tabular-nums">{fmtTime(c.last.timestamp)}</div>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className={`text-xs truncate ${c.unread > 0 ? 'text-ink' : 'text-ink-muted'}`}>
                          {isFromMe && <span className="text-ink-muted">Tú: </span>}
                          {c.last.content}
                        </div>
                        {c.unread > 0 && (
                          <span className="ml-auto text-[10px] font-bold text-white bg-primary rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1.5 shrink-0">
                            {c.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
};

// ─── Vista: Conversación ───────────────────────────────────────────────────
const Conversation = ({ otherId, messages, workersById, currentUser, onBack, onSend, onMarkRead, onAdoptSent }) => {
  const toast = useToast();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  // Mensajes que el OTRO ha recibido del par yo↔otro. Los traemos al abrir
  // el chat haciendo listMessages(otherId) y filtrando los nuestros.
  // Esto da persistencia real entre dispositivos: aunque haya enviado desde
  // otro PC, al abrir la conversación aquí vemos todo el historial.
  const [otherInbox, setOtherInbox] = useState([]);
  const scrollRef = useRef(null);
  const other = workersById.get(otherId);

  // Carga inicial + refresco cada 30 s del thread vía backend (sin localStorage).
  useEffect(() => {
    if (!otherId || !currentUser?.id) return;
    let cancelled = false;
    const fetchOther = async () => {
      try {
        const list = await listMessages(otherId);
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        // Solo nos interesan los que el OTRO recibió DE MÍ. Esos son los
        // mensajes que yo envié y que el backend ya tiene persistidos.
        const minePersisted = arr.filter((m) =>
          Number(m.senderId) === Number(currentUser.id) &&
          Number(m.receiverId) === Number(otherId)
        );
        setOtherInbox(minePersisted);
        // Promocionamos al hub para que el buzón los muestre aunque haya
        // perdido su localStorage (multi-dispositivo).
        if (typeof onAdoptSent === 'function' && minePersisted.length > 0) {
          onAdoptSent(minePersisted);
        }
      } catch {
        // sin red: caemos al state local del hub (received + sent cache).
      }
    };
    fetchOther();
    const i = setInterval(fetchOther, 30000);
    return () => { cancelled = true; clearInterval(i); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherId, currentUser?.id]);

  const thread = useMemo(() => {
    // Combina los mensajes del hub (received + sent cache) con los traídos
    // de listMessages(otherId). Deduplica por id.
    const merged = new Map();
    for (const m of messages) merged.set(m.id, m);
    for (const m of otherInbox) merged.set(m.id, m);
    return Array.from(merged.values())
      .filter((m) =>
        (Number(m.senderId) === Number(currentUser.id) && Number(m.receiverId) === Number(otherId)) ||
        (Number(m.receiverId) === Number(currentUser.id) && Number(m.senderId) === Number(otherId))
      )
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [messages, otherInbox, otherId, currentUser.id]);

  // Marcar como leídos los mensajes recibidos no leídos al abrir la conversación.
  useEffect(() => {
    const toRead = thread.filter((m) => !m.read && Number(m.receiverId) === Number(currentUser.id));
    if (toRead.length === 0) return;
    Promise.all(toRead.map((m) => readMessage(m.id).catch(() => {})))
      .then(() => onMarkRead && onMarkRead());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherId]);

  // Auto-scroll al fondo cuando llegan nuevos.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread.length]);

  const canReply = canMessage(currentUser, other || { id: otherId, type: 0, companyId: currentUser.companyId, workingSite: currentUser.workingSite });

  const handleSend = async () => {
    const content = text.trim();
    if (!content) return;
    setSending(true);
    setError(null);
    try {
      await onSend(otherId, content);
      toast.success(FEEDBACK.messageSent(other?.name));
      setText('');
    } catch (err) {
      setError(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-display font-bold text-ink truncate">{other?.name || `Usuario #${otherId}`}</div>
          {other?.email && <div className="text-[11px] text-ink-muted truncate">{other.email}</div>}
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
        {thread.map((m) => {
          const mine = Number(m.senderId) === Number(currentUser.id);
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                mine ? 'bg-primary text-white rounded-br-md' : 'bg-white text-ink border border-slate-200 rounded-bl-md'
              }`}>
                <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>
                <div className={`text-[10px] mt-0.5 tabular-nums ${mine ? 'text-white/70' : 'text-ink-muted'}`}>
                  {fmtDateTime(m.timestamp)}{mine && m.read ? ' · leído' : ''}
                </div>
              </div>
            </div>
          );
        })}
        {thread.length === 0 && (
          <div className="text-center text-xs text-ink-muted py-8">Aún no hay mensajes en esta conversación.</div>
        )}
      </div>
      {canReply ? (
        <div className="border-t border-slate-200 px-3 py-2">
          {error && (
            <div className="text-[11px] text-danger mb-1.5">{error}</div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Escribe un mensaje…"
              rows={2}
              className="flex-1 resize-none px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-primary text-sm"
            />
            <button
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="p-2 rounded-xl bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
              title="Enviar (Enter)"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-slate-200 px-3 py-3 text-center text-[11px] text-ink-muted">
          No puedes responder a esta conversación.
        </div>
      )}
    </>
  );
};

// ─── Vista: Nuevo mensaje (individual o colectivo) ─────────────────────────
const NewMessage = ({ candidates, workersById, currentUser, onBack, onSend, onSendCompany, onSendWorkplace }) => {
  const toast = useToast();
  const scopes = useMemo(() => broadcastScopes(currentUser), [currentUser]);
  const allowsCollective = scopes.workplace !== null || scopes.company !== null;
  // mode: 'individual' | 'workplace' | 'company'
  const [mode, setMode] = useState('individual');
  // Para super: seleccionar entre cualquier empresa o sede.
  const [companies, setCompanies] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [pickedCompany, setPickedCompany] = useState(null);
  const [pickedWorkplace, setPickedWorkplace] = useState(null);
  const [collectiveText, setCollectiveText] = useState('');
  const [collectiveSending, setCollectiveSending] = useState(false);
  const [collectiveError, setCollectiveError] = useState(null);

  // Carga lazy de empresas/sedes solo cuando el modo lo requiere y el rol permite 'any',
  // o cuando entramos en modo 'all' (necesitamos la lista para iterar).
  useEffect(() => {
    const needsCompanies =
      (mode === 'company' && scopes.company === 'any') ||
      (mode === 'workplace' && scopes.workplace === 'any') ||
      (mode === 'all' && scopes.allCompanies);
    if (needsCompanies && companies.length === 0) {
      searchCompanies('').then((list) => setCompanies(Array.isArray(list) ? list : [])).catch(() => {});
    }
  }, [mode, scopes.company, scopes.workplace, scopes.allCompanies, companies.length]);

  useEffect(() => {
    if (mode !== 'workplace' || !pickedCompany) return;
    setWorkplaces([]);
    setPickedWorkplace(null);
    getWorkplaces(pickedCompany.id).then((list) => setWorkplaces(Array.isArray(list) ? list : [])).catch(() => {});
  }, [mode, pickedCompany]);

  // Reset al cambiar de modo.
  useEffect(() => {
    setPickedCompany(null);
    setPickedWorkplace(null);
    setCollectiveText('');
    setCollectiveError(null);
  }, [mode]);

  const sendCollective = async () => {
    setCollectiveError(null);
    const content = collectiveText.trim();
    if (!content) return;
    setCollectiveSending(true);
    try {
      if (mode === 'company') {
        const cid = scopes.company === 'own' ? currentUser.companyId : pickedCompany?.id;
        if (cid == null) throw new Error('Selecciona una empresa.');
        await onSendCompany(cid, content);
        toast.success(FEEDBACK.messageSentCompany(scopes.company === 'own' ? null : pickedCompany?.name));
      } else if (mode === 'workplace') {
        const wid = scopes.workplace === 'own' ? currentUser.workingSite : pickedWorkplace?.id;
        if (wid == null) throw new Error('Selecciona una sede.');
        await onSendWorkplace(wid, content);
        toast.success(FEEDBACK.messageSentWorkplace(scopes.workplace === 'own' ? null : pickedWorkplace?.name));
      } else if (mode === 'all') {
        // Difusión a TODAS las empresas: confirmación obligatoria + iteración
        // por todas las empresas del sistema (la API no expone un endpoint
        // global, así que aquí orquestamos los N envíos).
        const ok = window.confirm(
          'Vas a enviar este mensaje a TODOS los usuarios de TODAS las empresas. ¿Continuar?'
        );
        if (!ok) { setCollectiveSending(false); return; }
        const list = companies.length > 0 ? companies : await searchCompanies('');
        const arr = Array.isArray(list) ? list : [];
        let ok_count = 0;
        let fail_count = 0;
        for (const c of arr) {
          try {
            await onSendCompany(c.id, content);
            ok_count += 1;
          } catch {
            fail_count += 1;
          }
        }
        if (ok_count === 0) throw new Error('No se pudo enviar a ninguna empresa.');
        // Un único resumen. Antes el aviso de fallos parciales iba a
        // `collectiveError`, pero `onBack()` cierra esta vista y se perdía.
        const summary = FEEDBACK.messageSentAll({ ok: ok_count, failed: fail_count });
        toast.show(summary.type, summary.message);
      }
      setCollectiveText('');
      onBack();
    } catch (err) {
      setCollectiveError(err.message || 'No se pudo enviar.');
    } finally {
      setCollectiveSending(false);
    }
  };

  if (allowsCollective && mode !== 'individual') {
    return (
      <CollectiveComposer
        mode={mode}
        scopes={scopes}
        currentUser={currentUser}
        companies={companies}
        workplaces={workplaces}
        pickedCompany={pickedCompany}
        pickedWorkplace={pickedWorkplace}
        onPickCompany={setPickedCompany}
        onPickWorkplace={setPickedWorkplace}
        text={collectiveText}
        onText={setCollectiveText}
        sending={collectiveSending}
        error={collectiveError}
        onChangeMode={setMode}
        onBack={onBack}
        onSend={sendCollective}
      />
    );
  }

  return <IndividualComposer
    candidates={candidates}
    onBack={onBack}
    onSend={onSend}
    allowsCollective={allowsCollective}
    onSwitchMode={setMode}
    scopes={scopes}
  />;
};

const IndividualComposer = ({ candidates, onBack, onSend, allowsCollective, onSwitchMode, scopes }) => {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.nif || '').toLowerCase().includes(q)
    );
  }, [candidates, query]);

  const handleSend = async () => {
    if (!picked || !text.trim()) return;
    setSending(true);
    setError(null);
    try {
      await onSend(picked.id, text.trim());
      toast.success(FEEDBACK.messageSent(picked.name));
      setText('');
      setPicked(null);
      onBack();
    } catch (err) {
      setError(err.message || 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
          <ChevronLeft size={18} />
        </button>
        <div className="text-sm font-display font-bold text-ink">Nuevo mensaje</div>
      </div>

      {allowsCollective && (
        <div className="px-3 pt-2 pb-1 border-b border-slate-200 flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => onSwitchMode('individual')}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-primary text-white"
          >
            <UserIcon size={11} /> Individual
          </button>
          {scopes.workplace !== null && (
            <button
              type="button"
              onClick={() => onSwitchMode('workplace')}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-ink-muted bg-slate-100 hover:bg-slate-200"
            >
              <MapPin size={11} /> {scopes.workplace === 'own' ? 'A mi sede' : 'A una sede'}
            </button>
          )}
          {scopes.company !== null && (
            <button
              type="button"
              onClick={() => onSwitchMode('company')}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-ink-muted bg-slate-100 hover:bg-slate-200"
            >
              <Building2 size={11} /> {scopes.company === 'own' ? 'A mi empresa' : 'A una empresa'}
            </button>
          )}
          {scopes.allCompanies && (
            <button
              type="button"
              onClick={() => onSwitchMode('all')}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-ink-muted bg-slate-100 hover:bg-slate-200"
            >
              <Globe size={11} /> A todas las empresas
            </button>
          )}
        </div>
      )}

      {!picked ? (
        <>
          <div className="px-3 py-2 border-b border-slate-200">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar destinatario por nombre, email o NIF"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-12 text-center text-sm text-ink-muted">
                {candidates.length === 0
                  ? 'No tienes destinatarios disponibles.'
                  : 'Ningún destinatario coincide con la búsqueda.'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setPicked(c)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-soft text-primary-darker flex items-center justify-center text-xs font-bold">
                        {(c.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink truncate">{c.name}</div>
                        <div className="text-[11px] text-ink-muted truncate">{c.email}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-soft text-primary-darker flex items-center justify-center text-xs font-bold">
              {(picked.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-ink truncate">{picked.name}</div>
              <div className="text-[11px] text-ink-muted truncate">{picked.email}</div>
            </div>
            <button onClick={() => setPicked(null)} className="text-[11px] text-ink-muted hover:text-ink underline">Cambiar</button>
          </div>
          <div className="flex-1 px-3 py-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escribe tu mensaje…"
              rows={6}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-primary text-sm resize-none"
              autoFocus
            />
            {error && <div className="text-[11px] text-danger mt-1.5">{error}</div>}
          </div>
          <div className="border-t border-slate-200 px-3 py-2 flex justify-end">
            <button
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Compositor colectivo (sede o empresa) ─────────────────────────────────
const CollectiveComposer = ({
  mode, scopes, currentUser, companies, workplaces,
  pickedCompany, pickedWorkplace, onPickCompany, onPickWorkplace,
  text, onText, sending, error, onChangeMode, onBack, onSend,
}) => {
  const isCompany = mode === 'company';
  const ownLabel = isCompany ? 'A toda mi empresa' : 'A todos los de mi sede';
  const Icon = isCompany ? Building2 : MapPin;

  // Para super en modo 'workplace' debe primero elegir empresa para acotar la lista de sedes.
  const showCompanyFirst = scopes[mode] === 'any' && (isCompany || (mode === 'workplace' && !pickedCompany));

  return (
    <>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <button onClick={onBack} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
          <ChevronLeft size={18} />
        </button>
        <div className="text-sm font-display font-bold text-ink">
          Mensaje colectivo · {mode === 'all' ? 'Todas las empresas' : isCompany ? 'Empresa' : 'Sede'}
        </div>
      </div>

      <div className="px-3 pt-2 pb-1 border-b border-slate-200 flex items-center gap-1.5 flex-wrap">
        <button onClick={() => onChangeMode('individual')}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-ink-muted bg-slate-100 hover:bg-slate-200">
          <UserIcon size={11} /> Individual
        </button>
        {scopes.workplace !== null && (
          <button onClick={() => onChangeMode('workplace')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
              mode === 'workplace' ? 'bg-primary text-white' : 'text-ink-muted bg-slate-100 hover:bg-slate-200'
            }`}>
            <MapPin size={11} /> {scopes.workplace === 'own' ? 'A mi sede' : 'A una sede'}
          </button>
        )}
        {scopes.company !== null && (
          <button onClick={() => onChangeMode('company')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
              mode === 'company' ? 'bg-primary text-white' : 'text-ink-muted bg-slate-100 hover:bg-slate-200'
            }`}>
            <Building2 size={11} /> {scopes.company === 'own' ? 'A mi empresa' : 'A una empresa'}
          </button>
        )}
        {scopes.allCompanies && (
          <button onClick={() => onChangeMode('all')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
              mode === 'all' ? 'bg-primary text-white' : 'text-ink-muted bg-slate-100 hover:bg-slate-200'
            }`}>
            <Globe size={11} /> Todas las empresas
          </button>
        )}
      </div>

      {/* Cabecera del destinatario colectivo */}
      <div className="px-4 py-3 border-b border-slate-200 bg-warning/5">
        <div className="flex items-center gap-2">
          <UsersIcon size={16} className="text-warning shrink-0" />
          <div className="flex-1 min-w-0">
            {mode === 'all' ? (
              <>
                <div className="text-sm font-semibold text-ink">A todas las empresas del sistema</div>
                <div className="text-[11px] text-ink-muted">
                  Se enviará a TODOS los usuarios de TODAS las empresas. Operación de superadmin —
                  pedirá confirmación antes de enviar.
                </div>
              </>
            ) : scopes[mode] === 'own' ? (
              <>
                <div className="text-sm font-semibold text-ink">{ownLabel}</div>
                <div className="text-[11px] text-ink-muted">
                  Se enviará a TODOS los usuarios {isCompany ? `de tu empresa` : `de tu sede`}.
                </div>
              </>
            ) : isCompany ? (
              <>
                <div className="text-sm font-semibold text-ink">
                  {pickedCompany ? `A toda «${pickedCompany.name}»` : 'Selecciona una empresa'}
                </div>
                <div className="text-[11px] text-ink-muted">Se enviará a TODOS los usuarios de la empresa seleccionada.</div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold text-ink">
                  {pickedWorkplace ? `A toda la sede «${pickedWorkplace.name}»` :
                   pickedCompany ? `Selecciona sede de «${pickedCompany.name}»` : 'Selecciona empresa y luego sede'}
                </div>
                <div className="text-[11px] text-ink-muted">Se enviará a TODOS los usuarios de la sede seleccionada.</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Selectores para 'any' (no se muestran en modo 'all'). */}
      {mode !== 'all' && scopes[mode] === 'any' && (
        <div className="px-3 py-2 border-b border-slate-200 space-y-2">
          {(isCompany || !pickedCompany) && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Empresa</label>
              <select
                value={pickedCompany?.id || ''}
                onChange={(e) => {
                  const c = companies.find((x) => String(x.id) === e.target.value) || null;
                  onPickCompany(c);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              >
                <option value="">Selecciona una empresa…</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          {!isCompany && pickedCompany && (
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-1">Sede</label>
              <select
                value={pickedWorkplace?.id || ''}
                onChange={(e) => {
                  const w = workplaces.find((x) => String(x.id) === e.target.value) || null;
                  onPickWorkplace(w);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              >
                <option value="">Selecciona una sede…</option>
                {workplaces.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Composer */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 px-3 py-3">
          <textarea
            value={text}
            onChange={(e) => onText(e.target.value)}
            placeholder="Escribe el mensaje colectivo…"
            rows={6}
            className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-primary text-sm resize-none"
          />
          {error && <div className="text-[11px] text-danger mt-1.5">{error}</div>}
        </div>
        <div className="border-t border-slate-200 px-3 py-2 flex justify-end">
          <button
            onClick={onSend}
            disabled={sending || !text.trim() || (mode !== 'all' && scopes[mode] === 'any' && (
              (isCompany && !pickedCompany) || (!isCompany && !pickedWorkplace)
            ))}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {mode === 'all' ? 'Enviar a TODAS las empresas' : 'Enviar a todos'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Hub principal: FAB + drawer + estado de mensajes ──────────────────────
const MessagingHub = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('inbox'); // inbox | conversation | new | newAnonymous | anonymousInbox
  const [activeOther, setActiveOther] = useState(null);
  const [received, setReceived] = useState([]);
  // Denuncias anónimas (solo si el user es admin empresa/sede).
  const [anonReceived, setAnonReceived] = useState([]);
  // Los enviados los guardamos aparte porque GET /api/message/list/:id solo
  // devuelve los recibidos. Sin esto, los chats en los que YO inicié la
  // conversación no aparecerían hasta que el otro respondiera. La caché
  // vive en localStorage (ver src/lib/sentMessagesCache.js) y se sincroniza
  // entre el hub y cualquier modal que también envíe mensajes (asignación
  // o edición de vacaciones por el admin, denuncias anónimas, etc.) gracias
  // al evento custom `tc:sent-message-appended` que dispara appendSentMessage.
  const [sent, setSent] = useState(() => readSent(user?.id));

  // Re-hidrata si cambia el usuario logueado y al recibir eventos de la
  // caché compartida (mismos tab o storage event de otra pestaña).
  useEffect(() => {
    if (!user?.id) { setSent([]); return; }
    setSent(readSent(user.id));
    const unsubscribe = subscribeToSent(user.id, () => {
      setSent(readSent(user.id));
    });
    return unsubscribe;
  }, [user?.id]);
  const [workers, setWorkers] = useState([]);
  const [loadError, setLoadError] = useState(null);

  // Mensajes combinados (recibidos + enviados), deduplicados por id.
  const messages = useMemo(() => {
    const m = new Map();
    for (const x of received) m.set(x.id, x);
    for (const x of sent) m.set(x.id, x);
    return Array.from(m.values());
  }, [received, sent]);

  const workersById = useMemo(() => {
    const m = new Map();
    for (const w of workers) m.set(Number(w.id), w);
    return m;
  }, [workers]);

  const candidates = useMemo(() => {
    if (!user) return [];
    return filterReceivers(user, workers);
  }, [user, workers]);

  // Solo destinatarios admin (type 1/2) — para el composer anónimo.
  const anonymousCandidates = useMemo(() => {
    if (!user) return [];
    return filterAnonymousReceivers(user, workers);
  }, [user, workers]);

  const refreshMessages = useCallback(async () => {
    if (!user?.id) return;
    try {
      const list = await listMessages(user.id);
      setReceived(Array.isArray(list) ? list : []);
      setLoadError(null);
    } catch (err) {
      setLoadError(err);
    }
  }, [user?.id]);

  const refreshAnonymousMessages = useCallback(async () => {
    if (!user?.id || !canReceiveAnonymous(user)) {
      setAnonReceived([]);
      return;
    }
    try {
      const list = await listAnonymousMessages(user.id);
      setAnonReceived(Array.isArray(list) ? list : []);
    } catch {
      // Si el endpoint falla, no es bloqueante para el resto del hub.
    }
  }, [user]);

  // Añade al state local los mensajes que YO acabo de enviar (la respuesta
  // del backend incluye el mensaje creado con su id, timestamp y read=false).
  // También usado por Conversation para "adoptar" mensajes ya persistidos
  // del par yo↔otro que descubre vía listMessages(otherId).
  // Delegamos en el helper compartido: actualiza localStorage y emite el
  // evento custom para que cualquier MessagingHub abierto en la misma o
  // en otra pestaña se refresque. El propio MessagingHub también escucha
  // ese evento (subscribeToSent), por lo que setSent se actualiza solo.
  const trackSent = useCallback((created) => {
    if (!user?.id) return;
    appendSentMessage(user.id, created);
  }, [user?.id]);

  // Cargar workers UNA vez al montar (índice de nombres).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    searchWorkers('').then((list) => {
      if (!cancelled) setWorkers(Array.isArray(list) ? list : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Polling del buzón cada 30 s + carga inicial.
  useEffect(() => {
    if (!user) return;
    refreshMessages();
    refreshAnonymousMessages();
    const int = setInterval(() => {
      refreshMessages();
      refreshAnonymousMessages();
    }, POLL_MS);
    return () => clearInterval(int);
  }, [user, refreshMessages, refreshAnonymousMessages]);

  const unreadCount = useMemo(
    () => messages.filter((m) => !m.read && Number(m.receiverId) === Number(user?.id)).length,
    [messages, user?.id]
  );

  const anonUnreadCount = useMemo(
    () => anonReceived.filter((m) => !m.read).length,
    [anonReceived]
  );

  const totalUnreadBadge = unreadCount + anonUnreadCount;

  const handleSend = async (otherId, content) => {
    const created = await sendMessage({ senderId: user.id, receiverId: otherId, content });
    trackSent(created);
  };

  const handleSendCompany = async (companyId, content) => {
    const created = await sendMessageToCompany({ senderId: user.id, companyId, content });
    trackSent(created);
  };

  const handleSendWorkplace = async (workplaceId, content) => {
    const created = await sendMessageToWorkplace({ senderId: user.id, workplaceId, content });
    trackSent(created);
  };

  // Denuncia anónima: no rastreamos el sent (el backend no lo guarda; el
  // remitente no debe ver historial para no comprometer el anonimato si
  // su dispositivo cae en manos del admin).
  const handleSendAnonymous = async (receiverId, content) => {
    await sendAnonymousMessage({ receiverId, content });
  };

  const handleOpenConversation = (otherId) => {
    setActiveOther(otherId);
    setView('conversation');
  };

  const handleNew = () => {
    setActiveOther(null);
    setView('new');
  };

  const handleNewAnonymous = () => {
    setActiveOther(null);
    setView('newAnonymous');
  };

  const handleOpenAnonymousInbox = () => {
    setActiveOther(null);
    setView('anonymousInbox');
  };

  const handleBack = () => {
    setView('inbox');
    setActiveOther(null);
  };

  if (!user) return null;

  return (
    <>
      {/* FAB siempre visible */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Abrir mensajes"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-primary text-white shadow-lg shadow-primary/40 hover:bg-primary-dark hover:shadow-xl transition-all flex items-center justify-center"
      >
        <MessageCircle size={22} />
        {totalUnreadBadge > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-danger text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
            {totalUnreadBadge > 99 ? '99+' : totalUnreadBadge}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
          />
          <aside className="fixed top-0 right-0 z-50 h-full w-full sm:w-[460px] md:w-[520px] lg:w-[560px] xl:w-[600px] bg-white shadow-2xl flex flex-col border-l border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
              <div className="flex items-center gap-2 text-ink">
                <MessageCircle size={18} className="text-primary" />
                <span className="font-display font-bold">Mensajería</span>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-slate-100 text-ink-muted">
                <X size={18} />
              </button>
            </div>

            {loadError && (
              <div className="m-3 text-[11px] text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                <span>{loadError.message || 'Error al cargar mensajes.'}</span>
              </div>
            )}

            <div className="flex-1 flex flex-col min-h-0">
              {view === 'inbox' && (
                <Inbox
                  messages={messages}
                  workersById={workersById}
                  currentUserId={user.id}
                  onOpenConversation={handleOpenConversation}
                  onNew={handleNew}
                  onNewAnonymous={handleNewAnonymous}
                  onOpenAnonymousInbox={handleOpenAnonymousInbox}
                  anonUnreadCount={anonUnreadCount}
                  showAnonymousSendButton={canSendAnonymous(user)}
                  showAnonymousInboxButton={canReceiveAnonymous(user)}
                />
              )}
              {view === 'conversation' && activeOther != null && (
                <Conversation
                  otherId={activeOther}
                  messages={messages}
                  workersById={workersById}
                  currentUser={user}
                  onBack={handleBack}
                  onSend={handleSend}
                  onMarkRead={refreshMessages}
                  onAdoptSent={trackSent}
                />
              )}
              {view === 'new' && (
                <NewMessage
                  candidates={candidates}
                  workersById={workersById}
                  currentUser={user}
                  onBack={handleBack}
                  onSend={handleSend}
                  onSendCompany={handleSendCompany}
                  onSendWorkplace={handleSendWorkplace}
                />
              )}
              {view === 'newAnonymous' && (
                <AnonymousComposer
                  candidates={anonymousCandidates}
                  currentUser={user}
                  onBack={handleBack}
                  onSend={handleSendAnonymous}
                />
              )}
              {view === 'anonymousInbox' && (
                <AnonymousInbox
                  anonMessages={anonReceived}
                  onBackToInbox={handleBack}
                  onMarkRead={refreshAnonymousMessages}
                />
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default MessagingHub;
